import React, { useState } from "react";
import { Segmented, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useIntl } from "react-intl";
import { useAuth } from "../../hooks/useAuth";
import { useGetImportPreviewQuery } from "../../services/nestjs-base-api";
import {
    ICustomerImportRow,
} from "../../services/nestjs-base-api";

const { Text } = Typography;

interface Props {
    jobId: number;
}

const PAGE_SIZE = 25;

export const ImportPreviewTable: React.FC<Props> = ({ jobId }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken ?? "";

    const [filter, setFilter] = useState<"all" | "invalid">("all");
    const [page, setPage] = useState(1);

    const { data, isFetching } = useGetImportPreviewQuery(
        {
            jwtToken,
            jobId,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            onlyInvalid: filter === "invalid",
        },
        { skip: !jwtToken },
    );

    const rows = data?.rows ?? [];
    const total = filter === "invalid"
        ? data?.invalid ?? 0
        : data?.total ?? 0;

    const columns: ColumnsType<ICustomerImportRow> = [
        {
            title: "#",
            dataIndex: "rowNumber",
            key: "rowNumber",
            width: 64,
        },
        {
            title: intl.formatMessage({ id: "customers.import.preview.col.status", defaultMessage: "Status" }),
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (status: ICustomerImportRow["status"]) => {
                const colourMap: Record<typeof status, string> = {
                    pending: "default",
                    valid: "green",
                    invalid: "red",
                    committed: "blue",
                    skipped: "default",
                };
                return <Tag color={colourMap[status]}>{status}</Tag>;
            },
        },
        {
            title: intl.formatMessage({ id: "customers.import.preview.col.parsed", defaultMessage: "Parsed values" }),
            key: "parsed",
            render: (_: unknown, r: ICustomerImportRow) => {
                if (!r.parsedData) {
                    return <Text type="secondary">—</Text>;
                }
                return (
                    <Space wrap size={4}>
                        {Object.entries(r.parsedData)
                            .filter(([, v]) => v != null)
                            .slice(0, 8)
                            .map(([k, v]) => (
                                <Tooltip key={k} title={`${k}: ${v}`}>
                                    <Tag>
                                        <Text style={{ fontSize: 12 }}>
                                            <Text strong>{k}</Text>: {String(v).slice(0, 30)}
                                        </Text>
                                    </Tag>
                                </Tooltip>
                            ))}
                    </Space>
                );
            },
        },
        {
            title: intl.formatMessage({ id: "customers.import.preview.col.errors", defaultMessage: "Errors" }),
            key: "errors",
            render: (_: unknown, r: ICustomerImportRow) => {
                if (!r.errors || r.errors.length === 0) {
                    return <Text type="secondary">—</Text>;
                }
                return (
                    <Space direction="vertical" size={2}>
                        {r.errors.map((e, idx) => (
                            <Text key={idx} type="danger" style={{ fontSize: 12 }}>
                                <Text strong>{e.field}</Text>: {e.message}
                            </Text>
                        ))}
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <div style={{ marginBottom: 12 }}>
                <Segmented
                    value={filter}
                    onChange={(v) => {
                        setPage(1);
                        setFilter(v as "all" | "invalid");
                    }}
                    options={[
                        {
                            label: intl.formatMessage(
                                { id: "customers.import.preview.filter.all", defaultMessage: "All ({n})" },
                                { n: data?.total ?? 0 },
                            ),
                            value: "all",
                        },
                        {
                            label: intl.formatMessage(
                                { id: "customers.import.preview.filter.invalid", defaultMessage: "Errors ({n})" },
                                { n: data?.invalid ?? 0 },
                            ),
                            value: "invalid",
                        },
                    ]}
                />
            </div>
            <Table<ICustomerImportRow>
                rowKey="id"
                size="small"
                loading={isFetching}
                columns={columns}
                dataSource={rows}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: setPage,
                }}
            />
        </>
    );
};
